import { Modal, Button, Row, Col, Alert, Table, Form, Spinner } from "react-bootstrap";
import { CheckCircle, XCircle } from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { formatCurrency } from "../../../store/config";

function SavingsRequestDetailsModal({
  show,
  onHide,
  request,
  processingAction,
  transactionReference,
  setTransactionReference,
  handleUpdateStatus,
  StatusBadge
}) {
  const { memberBalance } = useSelector((state) => state.admin);


  const getBalanceValue = (balance, code) => {
    const item = balance?.summary?.find(item => item.itemId.trim() === code);
    return item ? parseFloat(item.Balance) || 0 : 0;
  };
  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>Savings Withdrawal Request Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {request && (
          <>
            <div className="mb-4">
              <h6 className="border-bottom pb-2">Request Information</h6>
              <Row>
                <Col md={6}>
                  <small className="text-muted d-block">Request Date</small>
                  <div>{new Date(request.createdAt || request.requestDate).toLocaleDateString()}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted d-block">Status</small>
                  <StatusBadge status={request.status} />
                </Col>
                {request.comments && (
                  <Col xs={12} className="mt-3">
                    <Alert variant={request.status === "rejected" ? "danger" : "info"}>
                      <strong>Comments:</strong> {request.comments}
                    </Alert>
                  </Col>
                )}
                {request.transactionReference && (
                  <Col xs={12} className="mt-3">
                    <Alert variant="success">
                      <strong>Transaction Reference:</strong> {request.transactionReference}
                      {request.processedDate && (
                        <>
                          <br />
                          <strong>Processed Date:</strong> {new Date(request.processedDate).toLocaleDateString()}
                        </>
                      )}
                    </Alert>
                  </Col>
                )}
              </Row>
            </div>

            <div className="mb-4">
              <h6 className="border-bottom pb-2">Member Information</h6>
              <Row>
                <Col md={6} className="mb-2">
                  <small className="text-muted d-block">Member ID</small>
                  <div>{request.memberId || request.userId}</div>
                </Col>
                <Col md={6} className="mb-2">
                  <small className="text-muted d-block">Full Name</small>
                  <div>{request.personalInfo?.fullName || "N/A"}</div>
                </Col>
                <Col md={6} className="mb-2">
                  <small className="text-muted d-block">Email</small>
                  <div>{request.personalInfo?.email || "N/A"}</div>
                </Col>
                <Col md={6} className="mb-2">
                  <small className="text-muted d-block">Phone Number</small>
                  <div>{request.personalInfo?.phoneNumber || "N/A"}</div>
                </Col>
                {request.personalInfo?.faculty && (
                  <Col md={6} className="mb-2">
                    <small className="text-muted d-block">Faculty</small>
                    <div>{request.personalInfo.faculty}</div>
                  </Col>
                )}
                {request.personalInfo?.department && (
                  <Col md={6} className="mb-2">
                    <small className="text-muted d-block">Department</small>
                    <div>{request.personalInfo.department}</div>
                  </Col>
                )}
                {request.personalInfo?.salaryStructure && (
                  <Col md={12} className="mb-2">
                    <small className="text-muted d-block">Salary Structure</small>
                    <div>{request.personalInfo.salaryStructure}</div>
                  </Col>
                )}
              </Row>
            </div>

            <div className="mb-4">
              <h6 className="border-bottom pb-2">Bank Details</h6>
              <Row>
                <Col md={4} className="mb-2">
                  <small className="text-muted d-block">Account Name</small>
                  <div>{request.bankDetails?.accountName || "N/A"}</div>
                </Col>
                <Col md={4} className="mb-2">
                  <small className="text-muted d-block">Account Number</small>
                  <div>{request.bankDetails?.accountNumber || "N/A"}</div>
                </Col>
                <Col md={4} className="mb-2">
                  <small className="text-muted d-block">Bank Name</small>
                  <div>{request.bankDetails?.bankName || "N/A"}</div>
                </Col>
              </Row>
            </div>

            <div className="mb-4">
              <h6 className="border-bottom pb-2">Savings Withdrawal Details</h6>
              <Table bordered hover responsive className="mt-3">
                <thead className="table-light">
                  <tr>
                    <th>Savings Type</th>
                    <th className="text-right">Requested Amount</th>
                    <th className="text-right">Available Balance</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {request.withdrawalRequests?.map((item, index) => (
                    <tr key={index}>
                      <td>{item.savingsType}</td>
                      <td className="text-right">{formatCurrency(item.amount || 0)}</td>
                      <td className="text-right">
                        {memberBalance
                          ? formatCurrency(getBalanceValue(memberBalance, item.savingsCode) || 0)
                          : <Spinner animation="border" size="sm" />
                        }
                      </td>
                      <td className="text-center">
                        <StatusBadge status={item.status || request.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light">
                  <tr>
                    <th>Total</th>
                    <th className="text-right">
                      {formatCurrency(
                        request.withdrawalRequests?.reduce(
                          (sum, item) => sum + (item.amount || 0), 0
                        ) || 0
                      )}
                    </th>
                    <th className="text-right"></th>
                    <th></th>
                  </tr>
                </tfoot>
              </Table>
            </div>

            {/* Actions for pending requests */}
            {request.status === "pending" && (
              <div className="d-flex justify-content-end mt-4">
                <Button
                  variant="danger"
                  onClick={() => handleUpdateStatus("rejected")}
                  disabled={processingAction}
                  className="mr-2"
                >
                  <XCircle className="mr-1" /> Reject Request
                </Button>
                <Button
                  variant="success"
                  onClick={() => handleUpdateStatus("approved")}
                  disabled={processingAction}
                >
                  <CheckCircle className="mr-1" /> Approve Request
                </Button>
              </div>
            )}

            {/* Actions for approved requests */}
            {request.status === "approved" && (
              <div className="mt-4">
                <Form.Group className="mb-3">
                  <Form.Label>Transaction Reference</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter transaction reference or payment ID"
                    value={transactionReference}
                    onChange={(e) => setTransactionReference(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Enter the bank transaction reference or payment ID after processing the payment
                  </Form.Text>
                </Form.Group>
                <div className="d-flex justify-content-end">
                  <Button
                    variant="primary"
                    onClick={() => handleUpdateStatus("processed")}
                    disabled={processingAction || !transactionReference.trim()}
                  >
                    {processingAction ? (
                      <>
                        <Spinner animation="border" size="sm" className="mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>Mark as Processed</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default SavingsRequestDetailsModal;